import React from 'react';
import {
  Badge,
  Clickable,
  Icon,
  IconButton,
} from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';
import {
  ValidItemStage,
  VendorCatalogItem,
} from '../../../Lib/Hooks/ItemService';
import { itemStagesToVendorStages } from '../constants/stage.constants';

export const SidebarToggleButton = React.memo(
  ({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) => (
    <Clickable data-testid="sidebar-handler" onClick={onToggle}>
      <div className="text-base-disabled">
        {isOpen ? (
          <Icon.SidebarRightOpen size="18px" />
        ) : (
          <Icon.SidebarRightClose size="18px" />
        )}
      </div>
    </Clickable>
  ),
);

SidebarToggleButton.displayName = 'SidebarToggleButton';

export const StageBadge = React.memo(
  ({
    item,
    isError,
    isLoading,
    stage,
    badgeColor,
  }: {
    item: VendorCatalogItem | undefined;
    isError: boolean;
    isLoading: boolean;
    stage: ValidItemStage | undefined;
    badgeColor: 'primary' | 'purple' | 'red' | 'green' | 'neutral' | 'yellow';
  }) => {
    if (!item || isError || isLoading || !stage) return null;

    return (
      <Badge
        data-testid="stage-badge"
        contrast="low"
        color={badgeColor}
        size="lg"
      >
        {itemStagesToVendorStages[stage] || stage}
      </Badge>
    );
  },
);

StageBadge.displayName = 'StageBadge';

export const NavigationButtons = React.memo(
  ({
    targetItem,
    isError,
    isLoading,
    nextItemLink,
    previousItemLink,
    reorderedItems,
    currentItemsIndex,
  }: {
    targetItem: VendorCatalogItem | undefined;
    isError: boolean;
    isLoading: boolean;
    nextItemLink: string;
    previousItemLink: string;
    reorderedItems: VendorCatalogItem[] | null;
    currentItemsIndex: number | null;
  }) => (
    <div className="flex flex-row gap-2">
      <Link to={nextItemLink}>
        <IconButton
          data-testid="next-item-button"
          label="Next Item"
          size="md"
          color="secondary"
          icon={<Icon.ChevronDown />}
          disabled={
            !targetItem ||
            isError ||
            isLoading ||
            !reorderedItems ||
            currentItemsIndex === null ||
            currentItemsIndex < 0 ||
            currentItemsIndex === reorderedItems.length - 1
          }
        />
      </Link>
      <Link to={previousItemLink}>
        <IconButton
          data-testid="previous-item-button"
          label="Previous Item"
          size="md"
          color="secondary"
          icon={<Icon.ChevronUp />}
          disabled={
            !targetItem ||
            isError ||
            isLoading ||
            !reorderedItems ||
            currentItemsIndex === null ||
            currentItemsIndex < 1
          }
        />
      </Link>
    </div>
  ),
  (prevProps, nextProps) =>
    !(
      prevProps.nextItemLink !== nextProps.nextItemLink ||
      prevProps.previousItemLink !== nextProps.previousItemLink
    ),
);

NavigationButtons.displayName = 'NavigationButtons';

export const HeaderContent = React.memo(
  ({
    item,
    isError,
    isLoading,
    stage,
    badgeColor,
    nextItemLink,
    previousItemLink,
    reorderedItems,
    currentItemsIndex,
  }: {
    item: VendorCatalogItem | undefined;
    isError: boolean;
    isLoading: boolean;
    stage: ValidItemStage | undefined;
    badgeColor: 'primary' | 'purple' | 'red' | 'green' | 'neutral' | 'yellow';
    nextItemLink: string;
    previousItemLink: string;
    reorderedItems: VendorCatalogItem[] | null;
    currentItemsIndex: number | null;
  }) => (
    <div className="flex flex-row items-center gap-6">
      <StageBadge
        item={item}
        isError={isError}
        isLoading={isLoading}
        stage={stage}
        badgeColor={badgeColor}
      />
      <NavigationButtons
        targetItem={item}
        isError={isError}
        isLoading={isLoading}
        nextItemLink={nextItemLink}
        previousItemLink={previousItemLink}
        reorderedItems={reorderedItems}
        currentItemsIndex={currentItemsIndex}
      />
    </div>
  ),
);

HeaderContent.displayName = 'HeaderContent';
